import { useCompanySetupStore } from '@/stores/companySetup/companySetupStore'
import { Check, Loader2 } from 'lucide-react'

interface LoadingItemProps {
  label: string
  isLoading: boolean
  isComplete: boolean
}

function LoadingItem({ label, isLoading, isComplete }: LoadingItemProps) {
  return (
    <div className="flex items-center gap-3">
      {isComplete ? (
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500">
          <Check className="h-4 w-4 text-white" />
        </div>
      ) : isLoading ? (
        <Loader2 className="text-primary h-6 w-6 animate-spin" />
      ) : (
        <div className="bg-muted h-6 w-6 rounded-full" />
      )}
      <span
        className={`text-lg ${isComplete ? 'text-muted-foreground' : isLoading ? 'text-foreground' : 'text-muted-foreground'}`}>
        {label}
      </span>
    </div>
  )
}

export function CompanySetupLoadingStep() {
  const {
    isLoadingCompany,
    isLoadingLogos,
    isLoadingColors,
    companyAnalysis,
    logoCandidates,
    colorCandidates,
  } = useCompanySetupStore()

  const isCompanyComplete = !isLoadingCompany && companyAnalysis !== null
  const isLogosComplete = !isLoadingLogos && logoCandidates.length > 0
  const isColorsComplete = !isLoadingColors && colorCandidates.length > 0

  return (
    <div className="flex h-full flex-col px-4 py-[5vh] sm:py-0 sm:pt-[10vh]">
      <div className="mb-[10vh]">
        <h1 className="animate-fade-right text-3xl font-bold lg:text-5xl">
          Analyzing your brand...
        </h1>
        <p className="animate-fade-right text-muted-foreground mt-4 text-lg delay-200 lg:text-2xl">
          We're extracting information from your website. This may take a moment.
        </p>
      </div>

      <div className="animate-fade-up mx-auto flex w-full max-w-[400px] flex-col gap-6 delay-500">
        <LoadingItem
          label="Analyzing company information"
          isLoading={isLoadingCompany}
          isComplete={isCompanyComplete}
        />
        <LoadingItem
          label="Extracting logo candidates"
          isLoading={isLoadingLogos}
          isComplete={isLogosComplete}
        />
        <LoadingItem
          label="Extracting brand colors"
          isLoading={isLoadingColors}
          isComplete={isColorsComplete}
        />
      </div>
    </div>
  )
}
